/**
 * PROD-P0-005: Installation Instructions Component
 * Comprehensive step-by-step guide for installing the AIIA Connector
 * with OS-specific instructions and troubleshooting.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Server,
  Terminal,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  ExternalLink,
} from "lucide-react";

interface InstallationGuideProps {
  isOpen: boolean;
  onClose: () => void;
  instanceId?: string;
}

type OS = "linux" | "windows" | "macos";

export function InstallationGuide({
  isOpen,
  onClose,
  instanceId,
}: InstallationGuideProps) {
  const [selectedOS, setSelectedOS] = useState<OS>("linux");
  const [copiedSteps, setCopiedSteps] = useState<Set<number>>(new Set());

  const handleCopy = (text: string, stepNumber: number) => {
    navigator.clipboard.writeText(text);
    setCopiedSteps((prev) => new Set(prev).add(stepNumber));
    setTimeout(() => {
      setCopiedSteps((prev) => {
        const next = new Set(prev);
        next.delete(stepNumber);
        return next;
      });
    }, 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        aria-describedby="installation-guide-description"
        closeLabel="Close installation guide dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <div
              className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center"
              aria-hidden="true"
            >
              <Server className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <div>AIIA Connector Installation Guide</div>
              <div className="text-sm font-normal text-slate-300 mt-1">
                Step-by-step instructions for your Odoo server
              </div>
            </div>
          </DialogTitle>
          <DialogDescription id="installation-guide-description">
            Follow these instructions to install the AIIA Connector on your Odoo
            instance. The process takes approximately 5-10 minutes.
          </DialogDescription>
        </DialogHeader>

        {/* OS Selector */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">
              Select Your Operating System
            </label>
            <div className="flex gap-3">
              <Button
                variant={selectedOS === "linux" ? "default" : "outline"}
                onClick={() => setSelectedOS("linux")}
                className="flex-1"
              >
                <Terminal className="h-4 w-4 mr-2" aria-hidden="true" />
                Linux / Ubuntu
              </Button>
              <Button
                variant={selectedOS === "windows" ? "default" : "outline"}
                onClick={() => setSelectedOS("windows")}
                className="flex-1"
              >
                <Server className="h-4 w-4 mr-2" aria-hidden="true" />
                Windows Server
              </Button>
              <Button
                variant={selectedOS === "macos" ? "default" : "outline"}
                onClick={() => setSelectedOS("macos")}
                className="flex-1"
              >
                <Server className="h-4 w-4 mr-2" aria-hidden="true" />
                macOS
              </Button>
            </div>
          </div>

          {/* Prerequisites */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-6">
            <h3 className="font-semibold text-slate-100 mb-3 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
              Prerequisites
            </h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>Odoo 17, 18, or 19 installed and running</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>SSH access to your Odoo server</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>Write permissions to Odoo addons directory</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>Internet connectivity for downloading the installer</span>
              </li>
            </ul>
          </div>

          {/* Installation Steps */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-100">
              Installation Steps
            </h3>

            {/* Linux Instructions */}
            {selectedOS === "linux" && (
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge className="mb-2">Step 1</Badge>
                      <h4 className="font-semibold text-slate-100">
                        Download the Installer
                      </h4>
                      <p className="text-sm text-slate-300 mt-1">
                        Use wget or curl to download the pre-configured installer
                      </p>
                    </div>
                  </div>
                  <div className="bg-slate-900 rounded-md p-4 font-mono text-sm">
                    <div className="flex items-start justify-between">
                      <code className="text-emerald-400 flex-1">
                        wget https://admin.aiia.io/api/client/instances/
                        {instanceId || "YOUR_INSTANCE_ID"}/download
                        <br />
                        chmod +x install.sh
                      </code>
                      <button
                        onClick={() =>
                          handleCopy(
                            `wget https://admin.aiia.io/api/client/instances/${instanceId || "YOUR_INSTANCE_ID"}/download\nchmod +x install.sh`,
                            1
                          )
                        }
                        className="ml-2 p-2 hover:bg-slate-800 rounded transition-colors"
                        aria-label="Copy command"
                      >
                        {copiedSteps.has(1) ? (
                          <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-300" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge className="mb-2">Step 2</Badge>
                      <h4 className="font-semibold text-slate-100">
                        Run the Installer
                      </h4>
                      <p className="text-sm text-slate-300 mt-1">
                        Execute the installation script with appropriate permissions
                      </p>
                    </div>
                  </div>
                  <div className="bg-slate-900 rounded-md p-4 font-mono text-sm">
                    <div className="flex items-start justify-between">
                      <code className="text-emerald-400 flex-1">
                        sudo bash install.sh
                      </code>
                      <button
                        onClick={() => handleCopy("sudo bash install.sh", 2)}
                        className="ml-2 p-2 hover:bg-slate-800 rounded transition-colors"
                        aria-label="Copy command"
                      >
                        {copiedSteps.has(2) ? (
                          <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-300" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-slate-300">
                    <strong className="text-slate-300">Note:</strong> The installer
                    will:
                    <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                      <li>Verify your Odoo installation</li>
                      <li>Download the AIIA Connector module</li>
                      <li>Install dependencies</li>
                      <li>Configure the connector with your instance settings</li>
                    </ul>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge className="mb-2">Step 3</Badge>
                      <h4 className="font-semibold text-slate-100">
                        Restart Odoo Service
                      </h4>
                      <p className="text-sm text-slate-300 mt-1">
                        Restart Odoo to load the new connector module
                      </p>
                    </div>
                  </div>
                  <div className="bg-slate-900 rounded-md p-4 font-mono text-sm">
                    <div className="flex items-start justify-between">
                      <code className="text-emerald-400 flex-1">
                        sudo systemctl restart odoo
                        <br />
                        sudo systemctl status odoo
                      </code>
                      <button
                        onClick={() =>
                          handleCopy(
                            "sudo systemctl restart odoo\nsudo systemctl status odoo",
                            3
                          )
                        }
                        className="ml-2 p-2 hover:bg-slate-800 rounded transition-colors"
                        aria-label="Copy command"
                      >
                        {copiedSteps.has(3) ? (
                          <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-300" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge className="mb-2">Step 4</Badge>
                      <h4 className="font-semibold text-slate-100">
                        Activate the Module in Odoo
                      </h4>
                      <p className="text-sm text-slate-300 mt-1">
                        Install the AIIA Connector from the Odoo Apps menu
                      </p>
                    </div>
                  </div>
                  <ol className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/10 text-blue-400 font-semibold flex-shrink-0">
                        1
                      </span>
                      <span>
                        Log in to your Odoo instance as an administrator
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/10 text-blue-400 font-semibold flex-shrink-0">
                        2
                      </span>
                      <span>Navigate to Apps → Update Apps List</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/10 text-blue-400 font-semibold flex-shrink-0">
                        3
                      </span>
                      <span>
                        Search for "AIIA Connector" in the Apps search box
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/10 text-blue-400 font-semibold flex-shrink-0">
                        4
                      </span>
                      <span>Click the "Install" button</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/10 text-blue-400 font-semibold flex-shrink-0">
                        5
                      </span>
                      <span>
                        Wait for installation to complete (1-2 minutes)
                      </span>
                    </li>
                  </ol>
                  <div className="mt-4 bg-emerald-500/5 border border-emerald-500/20 rounded-md p-3">
                    <p className="text-sm text-emerald-400 flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>
                        The connector will automatically phone home to register with
                        the AIIA Admin Portal
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Windows Instructions */}
            {selectedOS === "windows" && (
              <div className="space-y-6">
                <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-6">
                  <h4 className="font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" aria-hidden="true" />
                    Windows Installation Note
                  </h4>
                  <p className="text-sm text-slate-300">
                    Odoo on Windows Server requires PowerShell or Git Bash. The
                    installation process is similar to Linux but uses Windows-specific
                    paths.
                  </p>
                </div>

                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                  <Badge className="mb-2">Step 1</Badge>
                  <h4 className="font-semibold text-slate-100 mb-3">
                    Download via PowerShell
                  </h4>
                  <div className="bg-slate-900 rounded-md p-4 font-mono text-sm">
                    <div className="flex items-start justify-between">
                      <code className="text-blue-400 flex-1">
                        Invoke-WebRequest -Uri
                        "https://admin.aiia.io/api/client/instances/
                        {instanceId || "YOUR_INSTANCE_ID"}/download" -OutFile
                        "install.sh"
                      </code>
                      <button
                        onClick={() =>
                          handleCopy(
                            `Invoke-WebRequest -Uri "https://admin.aiia.io/api/client/instances/${instanceId || "YOUR_INSTANCE_ID"}/download" -OutFile "install.sh"`,
                            11
                          )
                        }
                        className="ml-2 p-2 hover:bg-slate-800 rounded transition-colors"
                        aria-label="Copy command"
                      >
                        {copiedSteps.has(11) ? (
                          <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-300" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-slate-300">
                  <p>
                    Follow steps 2-4 from the Linux instructions above, using Git Bash
                    or WSL to run the bash script. Alternatively, extract the installer
                    contents and manually copy the module to your Odoo addons directory:
                  </p>
                  <div className="mt-3 bg-slate-900 rounded-md p-3 font-mono text-xs">
                    <code className="text-blue-400">
                      C:\Program Files\Odoo\server\odoo\addons\
                    </code>
                  </div>
                </div>
              </div>
            )}

            {/* macOS Instructions */}
            {selectedOS === "macos" && (
              <div className="space-y-6">
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                  <p className="text-sm text-slate-300">
                    macOS installation follows the same process as Linux. Use Terminal
                    to execute the bash commands. Ensure you have Homebrew installed for
                    any missing dependencies.
                  </p>
                </div>

                <div className="text-sm text-slate-300">
                  <p className="mb-3">Follow the Linux instructions above. If you encounter permission issues:</p>
                  <div className="bg-slate-900 rounded-md p-4 font-mono text-sm">
                    <div className="flex items-start justify-between">
                      <code className="text-emerald-400 flex-1">
                        sudo chown -R $(whoami) /path/to/odoo/addons
                        <br />
                        bash install.sh
                      </code>
                      <button
                        onClick={() =>
                          handleCopy(
                            "sudo chown -R $(whoami) /path/to/odoo/addons\nbash install.sh",
                            21
                          )
                        }
                        className="ml-2 p-2 hover:bg-slate-800 rounded transition-colors"
                        aria-label="Copy command"
                      >
                        {copiedSteps.has(21) ? (
                          <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-300" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Troubleshooting */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-100">
              Troubleshooting Common Issues
            </h3>

            <div className="space-y-3">
              <details className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <summary className="font-medium text-slate-100 cursor-pointer hover:text-slate-50">
                  ❌ Permission Denied Error
                </summary>
                <div className="mt-3 text-sm text-slate-300 space-y-2">
                  <p>
                    <strong className="text-slate-300">Solution:</strong> Ensure you
                    have write permissions to the Odoo addons directory:
                  </p>
                  <div className="bg-slate-900 rounded-md p-3 font-mono text-xs">
                    <code className="text-emerald-400">
                      sudo chown -R odoo:odoo /opt/odoo/custom-addons
                    </code>
                  </div>
                </div>
              </details>

              <details className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <summary className="font-medium text-slate-100 cursor-pointer hover:text-slate-50">
                  ❌ Module Not Found in Odoo Apps
                </summary>
                <div className="mt-3 text-sm text-slate-300 space-y-2">
                  <p>
                    <strong className="text-slate-300">Solution:</strong> Make sure
                    you updated the apps list:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Go to Apps → Update Apps List</li>
                    <li>Clear the search filters</li>
                    <li>Search for "AIIA"</li>
                  </ol>
                </div>
              </details>

              <details className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <summary className="font-medium text-slate-100 cursor-pointer hover:text-slate-50">
                  ❌ Connector Not Connecting to Admin Portal
                </summary>
                <div className="mt-3 text-sm text-slate-300 space-y-2">
                  <p>
                    <strong className="text-slate-300">Solution:</strong> Check
                    network connectivity:
                  </p>
                  <div className="bg-slate-900 rounded-md p-3 font-mono text-xs">
                    <code className="text-emerald-400">
                      curl -I https://admin.aiia.io/health
                    </code>
                  </div>
                  <p className="mt-2">
                    If the health check fails, verify your firewall allows outbound
                    HTTPS connections to admin.aiia.io
                  </p>
                </div>
              </details>

              <details className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <summary className="font-medium text-slate-100 cursor-pointer hover:text-slate-50">
                  ❌ Installation Timeout or Hangs
                </summary>
                <div className="mt-3 text-sm text-slate-300 space-y-2">
                  <p>
                    <strong className="text-slate-300">Solution:</strong> Check Odoo
                    logs for errors:
                  </p>
                  <div className="bg-slate-900 rounded-md p-3 font-mono text-xs">
                    <code className="text-emerald-400">
                      tail -f /var/log/odoo/odoo-server.log
                    </code>
                  </div>
                  <p className="mt-2">
                    Look for Python import errors or missing dependencies. Install any
                    missing packages with pip:
                  </p>
                  <div className="bg-slate-900 rounded-md p-3 font-mono text-xs mt-2">
                    <code className="text-emerald-400">
                      sudo pip3 install -r
                      /opt/odoo/custom-addons/aiia_connector/requirements.txt
                    </code>
                  </div>
                </div>
              </details>
            </div>
          </div>

          {/* Help Resources */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-6">
            <h3 className="font-semibold text-slate-100 mb-3">
              Need More Help?
            </h3>
            <div className="space-y-3 text-sm">
              <a
                href="https://docs.aiia.io/connector/installation"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                View Full Documentation
                <span className="sr-only">(opens in new tab)</span>
              </a>
              <a
                href="https://support.aiia.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                Contact Support
                <span className="sr-only">(opens in new tab)</span>
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-slate-700">
          <Button onClick={onClose}>Close Guide</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
